import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../modules/auth/firebaseConfig';
import BlogPostCard from './BlogPostCard';

export default function BlogPostList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search UI (match AdminBlogEditor style)
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Pagination
  const PAGE_SIZE = 12;
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const q = query(
          collection(db, 'post'),
          where('visibility', '==', 'public'),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q);

        const data = snapshot.docs.map((docSnap) => {
          const postData = docSnap.data();

          // Format createdAt to readable date
          let formattedDate = '';
          if (postData.createdAt?.toDate) {
            formattedDate = postData.createdAt.toDate().toLocaleDateString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            });
          } else if (postData.createdAt?.seconds) {
            formattedDate = new Date(postData.createdAt.seconds * 1000).toLocaleDateString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            });
          }

          return {
            id: docSnap.id,
            ...postData,
            formattedDate,
          };
        });

        setPosts(data);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        inputRef.current && !inputRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter by search
  const filteredPosts = useMemo(
    () =>
      posts.filter((p) =>
        (p.caption || '').toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [posts, searchTerm]
  );

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const visiblePosts = filteredPosts.slice(start, start + PAGE_SIZE);

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  if (loading) return <p className="text-center py-10 text-white">Loading posts...</p>;
  if (!loading && posts.length === 0) {
    return <p className="text-center py-10 text-white">No public posts found.</p>;
  }

  return (
    <div className="min-h-screen px-4 py-10 bg-black text-white">
      {/* SEARCH (same vibe as AdminBlogEditor) */}
      <section className="text-center py-4 relative">
        
        <div className="relative max-w-md mt-6 mx-auto" ref={inputRef}>
          <div className="flex justify-left bg-gray-900 rounded-md overflow-hidden">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setShowSuggestions(true); }}
              onClick={() => setShowSuggestions(true)}
              placeholder="Search posts..."
              className="w-full px-4 py-2 bg-gray-900 text-white focus:outline-none"
            />
            <button
              className="px-3 py-2 bg-gray-800 text-white"
              onClick={() => setShowSuggestions((s) => !s)}
            >
              ▼
            </button>
          </div>

          {showSuggestions && (
            <ul
              ref={dropdownRef}
              className="absolute left-0 w-full bg-gray-800 border border-t-0 border-gray-600 rounded-b-md shadow-md z-10 max-h-60 overflow-auto"
              style={{ top: '100%', marginTop: 0 }}
            >
              {posts.map((p) => (
                <li
                  key={p.id}
                  onClick={() => { setSearchTerm(p.caption || ''); setShowSuggestions(false); }}
                  className="px-4 py-2 hover:bg-blue-700 cursor-pointer text-left text-white"
                >
                  {p.caption}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* GRID */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
        {visiblePosts.map((post) => (
          <BlogPostCard key={post.id} id={post.id} post={post} />
        ))}
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-8 max-w-4xl mx-auto">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className={`px-4 py-2 rounded border border-gray-700 ${page === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'}`}
          >
            ← Prev
          </button>

          <span className="text-sm text-gray-300">
            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
          </span>

          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className={`px-4 py-2 rounded border border-gray-700 ${page === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'}`}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
